class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.onHistoryReturn = (event) => {
      if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
        this.refreshCart();
      }
    };
  }

  connectedCallback() {
    window.addEventListener('pageshow', this.onHistoryReturn);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onHistoryReturn);
  }

  async openFromHeader(triggeredBy) {
    if (this.openingFromHeader) return;
    this.openingFromHeader = true;
    triggeredBy.setAttribute('aria-busy', 'true');
    try {
      if (await this.refreshCart()) {
        this.open(triggeredBy);
      } else {
        window.location.assign(routes.cart_url);
      }
    } finally {
      this.openingFromHeader = false;
      triggeredBy.removeAttribute('aria-busy');
    }
  }

  refreshCart() {
    if (this.refreshPromise) return this.refreshPromise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    this.refreshPromise = fetch(url, { cache: 'no-store', signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Cart refresh failed');
        return response.json();
      })
      .then((sections) => {
        const parser = new DOMParser();
        const sourceDrawer = parser.parseFromString(sections['cart-drawer'] || '', 'text/html').querySelector('cart-drawer');
        const sourceCart = sourceDrawer?.querySelector('#CartDrawer');
        const sourceIcon = parser.parseFromString(sections['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
        const targetCart = this.querySelector('#CartDrawer');
        const targetIcon = document.getElementById('cart-icon-bubble');
        if (!sourceCart || !sourceIcon || !targetCart || !targetIcon) return false;
        const isOpen = this.classList.contains('active');
        if (isOpen) removeTrapFocus();
        this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        targetCart.innerHTML = sourceCart.innerHTML;
        targetIcon.innerHTML = sourceIcon.innerHTML;
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        if (isOpen) {
          const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : targetCart;
          trapFocus(container, this.querySelector('.drawer__inner'));
        }
        return true;
      })
      .catch(() => false)
      .finally(() => {
        clearTimeout(timeout);
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openFromHeader(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openFromHeader(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

